import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

import jobOfferService from "../../services/jobOfferService";
import interviewService from "../../services/interviewService";
import apiClient from "../../services/api_client";

import { getErrorMessage } from "../../utils/errorHandler";

import {
  User,
  Briefcase,
  Sparkles,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import "../../styles/recruitment.css";


const InterviewPreparation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ==========================================================
  // ÉTATS
  // ==========================================================

  const [jobOffers, setJobOffers] = useState([]);
  const [candidates, setCandidates] = useState([]);

  const [selectedJobOffer, setSelectedJobOffer] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [preparation, setPreparation] = useState(null);

  const [customRequirements, setCustomRequirements] =
    useState("");

  const [expandedCategories, setExpandedCategories] =
    useState({});


  // ==========================================================
  // UTILITAIRES
  // ==========================================================

  /**
   * Normalise les différentes formes de réponse API.
   *
   * Possibilités :
   *
   * [
   *   {...}
   * ]
   *
   * ou :
   *
   * {
   *   data: [...]
   * }
   *
   * ou :
   *
   * {
   *   items: [...]
   * }
   */
  const normalizeList = (response) => {
    let payload = response;

    // Axios response
    if (
      payload &&
      typeof payload === "object" &&
      Object.prototype.hasOwnProperty.call(
        payload,
        "data"
      ) &&
      (
        Array.isArray(payload.data) ||
        (
          payload.data &&
          typeof payload.data === "object"
        )
      )
    ) {
      payload = payload.data;
    }

    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    if (Array.isArray(payload?.items)) {
      return payload.items;
    }

    return [];
  };


  /**
   * Récupère les données réelles d'une réponse API.
   */
  const unwrapResponse = (response) => {
    if (
      response &&
      typeof response === "object" &&
      response.data !== undefined
    ) {
      return response.data;
    }

    return response;
  };


  // ==========================================================
  // CHARGEMENT DES OFFRES
  // ==========================================================

  const loadJobOffers = async () => {
    try {
      const response =
        await jobOfferService.getAll();

      console.log(
        "[InterviewPreparation] Réponse offres :",
        response
      );

      const offers = normalizeList(response);

      console.log(
        "[InterviewPreparation] Offres normalisées :",
        offers
      );

      const openOffers = offers.filter((offer) => {
        const status = String(
          offer?.status || ""
        )
          .trim()
          .toUpperCase();

        return status === "OPEN";
      });

      console.log(
        "[InterviewPreparation] Offres ouvertes :",
        openOffers
      );

      setJobOffers(openOffers);

      return openOffers;

    } catch (err) {
      console.error(
        "[InterviewPreparation] Erreur offres :",
        err?.response?.data || err
      );

      setJobOffers([]);

      throw err;
    }
  };


  // ==========================================================
  // CHARGEMENT DES CANDIDATS
  // ==========================================================

  const loadCandidates = async () => {
    try {
      /**
       * IMPORTANT :
       *
       * Il n'existe pas de :
       *
       * /recruitment/candidates
       *
       * On utilise donc :
       *
       * /recruitment/applications
       *
       * puis on extrait application.candidate.
       */

      const response =
        await apiClient.get(
          "/recruitment/applications"
        );

      console.log(
        "[InterviewPreparation] Réponse candidatures :",
        response
      );

      const applications =
        normalizeList(response);

      console.log(
        "[InterviewPreparation] Candidatures normalisées :",
        applications
      );


      // ======================================================
      // EXTRAIRE LES CANDIDATS
      // ======================================================

      const extractedCandidates = applications
        .map((application) => {

          // --------------------------------------------------
          // Structure :
          // application.candidate
          // --------------------------------------------------

          if (application?.candidate) {
            return application.candidate;
          }


          // --------------------------------------------------
          // Structure :
          // application.candidat
          // --------------------------------------------------

          if (application?.candidat) {
            return application.candidat;
          }


          // --------------------------------------------------
          // Structure aplatie
          // --------------------------------------------------

          if (
            application?.candidate_id !== undefined &&
            application?.candidate_id !== null
          ) {
            return {
              id: application.candidate_id,

              first_name:
                application.first_name ||
                application.prenom ||
                "",

              last_name:
                application.last_name ||
                application.nom ||
                "",

              email:
                application.email ||
                "",

              phone:
                application.phone ||
                application.telephone ||
                "",

              status:
                application.status ||
                "PENDING",

              cv_path:
                application.cv_path ||
                null,
            };
          }

          return null;
        })
        .filter(
          (candidate) =>
            candidate &&
            candidate.id !== undefined &&
            candidate.id !== null
        );


      // ======================================================
      // SUPPRIMER LES DOUBLONS
      // ======================================================

      const uniqueCandidates =
        Array.from(
          new Map(
            extractedCandidates.map(
              (candidate) => [
                candidate.id,
                candidate,
              ]
            )
          ).values()
        );


      console.log(
        "[InterviewPreparation] Candidats extraits :",
        extractedCandidates
      );

      console.log(
        "[InterviewPreparation] Candidats uniques :",
        uniqueCandidates
      );


      setCandidates(
        uniqueCandidates
      );

      return uniqueCandidates;

    } catch (err) {

      console.error(
        "[InterviewPreparation] Erreur candidats :",
        err?.response?.data || err
      );

      setCandidates([]);

      throw err;
    }
  };


  // ==========================================================
  // CHARGEMENT INITIAL
  // ==========================================================

  const loadData = async () => {
    setLoading(true);
    setError("");

    let offersLoaded = false;
    let candidatesLoaded = false;

    // --------------------------------------------------------
    // OFFRES
    // --------------------------------------------------------

    try {
      await loadJobOffers();
      offersLoaded = true;
    } catch (err) {
      console.error(
        "[InterviewPreparation] Impossible de charger les offres.",
        err
      );
    }


    // --------------------------------------------------------
    // CANDIDATS
    // --------------------------------------------------------

    try {
      await loadCandidates();
      candidatesLoaded = true;
    } catch (err) {
      console.error(
        "[InterviewPreparation] Impossible de charger les candidats.",
        err
      );
    }


    // --------------------------------------------------------
    // MESSAGE
    // --------------------------------------------------------

    if (
      !offersLoaded &&
      !candidatesLoaded
    ) {
      setError(
        "Impossible de charger les offres et les candidats."
      );
    } else if (!offersLoaded) {
      setError(
        "Les candidats sont chargés, mais les offres ne sont pas disponibles."
      );
    } else if (!candidatesLoaded) {
      /**
       * Ce message n'efface PAS les offres.
       */
      setError(
        "Les offres sont disponibles, mais aucune liste de candidats n'a pu être chargée."
      );
    }

    setLoading(false);
  };


  // ==========================================================
  // USE EFFECT
  // ==========================================================

  useEffect(() => {
    loadData();
  }, []);


  // ==========================================================
  // GENERATION DE LA PREPARATION
  // ==========================================================

  const handleGeneratePreparation = async () => {

    if (
      !selectedJobOffer &&
      !selectedCandidate
    ) {
      setError(
        "Veuillez sélectionner au moins une offre d'emploi ou un candidat."
      );

      return;
    }


    try {

      setGenerating(true);

      setError("");

      setSuccess("");


      // ------------------------------------------------------
      // OFFRE SÉLECTIONNÉE
      // ------------------------------------------------------

      const selectedOffer =
        selectedJobOffer
          ? jobOffers.find(
              (offer) =>
                Number(offer.id) ===
                Number(selectedJobOffer)
            )
          : null;


      // ------------------------------------------------------
      // CANDIDAT SÉLECTIONNÉ
      // ------------------------------------------------------

      const selectedCandidateObject =
        selectedCandidate
          ? candidates.find(
              (candidate) =>
                Number(candidate.id) ===
                Number(selectedCandidate)
            )
          : null;


      // ------------------------------------------------------
      // PAYLOAD
      // ------------------------------------------------------

      const payload = {

        job_offer_id:
          selectedJobOffer
            ? Number(selectedJobOffer)
            : null,

        candidate_id:
          selectedCandidate
            ? Number(selectedCandidate)
            : null,

        job_title:
          selectedOffer?.title ||
          null,

        custom_requirements:
          customRequirements.trim() ||
          null,
      };


      console.log(
        "[InterviewPreparation] Offre sélectionnée :",
        selectedOffer
      );

      console.log(
        "[InterviewPreparation] Candidat sélectionné :",
        selectedCandidateObject
      );

      console.log(
        "[InterviewPreparation] Payload génération :",
        payload
      );


      // ------------------------------------------------------
      // APPEL IA
      // ------------------------------------------------------

      const result =
        await interviewService.prepareInterview(
          payload
        );


      /**
       * Le service peut retourner :
       *
       * result
       *
       * ou :
       *
       * { data: result }
       *
       * On normalise.
       */

      const preparationData =
        unwrapResponse(result);


      console.log(
        "[InterviewPreparation] Résultat préparation :",
        preparationData
      );


      setPreparation(
        preparationData
      );


      setSuccess(
        "Entretien généré avec succès !"
      );


      // ------------------------------------------------------
      // OUVRIR LES CATEGORIES
      // ------------------------------------------------------

      if (
        preparationData?.categories_distribution
      ) {

        const expanded = {};

        Object.keys(
          preparationData.categories_distribution
        ).forEach((category) => {
          expanded[category] = true;
        });

        setExpandedCategories(
          expanded
        );
      }

    } catch (err) {

      console.error(
        "[InterviewPreparation] Erreur génération :",
        err?.response?.data || err
      );

      setError(
        getErrorMessage(err)
      );

    } finally {

      setGenerating(false);

    }
  };


  // ==========================================================
  // TOGGLE CATEGORY
  // ==========================================================

  const toggleCategory = (
    category
  ) => {

    setExpandedCategories(
      (previous) => ({
        ...previous,

        [category]:
          !previous[category],
      })
    );
  };


  // ==========================================================
  // LABEL CATEGORY
  // ==========================================================

  const getCategoryLabel = (
    category
  ) => {

    const labels = {

      TECHNICAL:
        "Questions techniques",

      BEHAVIORAL:
        "Questions comportementales",

      EXPERIENCE:
        "Questions sur l'expérience",

      PROJECT:
        "Questions sur les projets",

      JOB_SPECIFIC:
        "Questions spécifiques à l'offre",

      SKILL_VERIFICATION:
        "Questions de vérification",
    };

    return (
      labels[category] ||
      category
    );
  };


  // ==========================================================
  // COULEUR CATEGORY
  // ==========================================================

  const getCategoryColor = (
    category
  ) => {

    const colors = {

      TECHNICAL:
        "#3b82f6",

      BEHAVIORAL:
        "#10b981",

      EXPERIENCE:
        "#f59e0b",

      PROJECT:
        "#8b5cf6",

      JOB_SPECIFIC:
        "#ec4899",

      SKILL_VERIFICATION:
        "#6366f1",
    };

    return (
      colors[category] ||
      "#6b7280"
    );
  };


  // ==========================================================
  // LABEL DIFFICULTY
  // ==========================================================

  const getDifficultyLabel = (
    difficulty
  ) => {

    const labels = {

      EASY:
        "Facile",

      MEDIUM:
        "Moyen",

      HARD:
        "Difficile",
    };

    return (
      labels[difficulty] ||
      difficulty ||
      "-"
    );
  };


  // ==========================================================
  // COULEUR DIFFICULTY
  // ==========================================================

  const getDifficultyColor = (
    difficulty
  ) => {

    const colors = {

      EASY:
        "#10b981",

      MEDIUM:
        "#f59e0b",

      HARD:
        "#ef4444",
    };

    return (
      colors[difficulty] ||
      "#6b7280"
    );
  };


  // ==========================================================
  // NOUVELLE PREPARATION
  // ==========================================================

  const handleNewPreparation = () => {

    setPreparation(null);

    setSelectedJobOffer(null);

    setSelectedCandidate(null);

    setCustomRequirements("");

    setExpandedCategories({});

    setError("");

    setSuccess("");
  };


  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {

    return (
      <div
        style={{
          padding: "24px",
        }}
      >
        <div className="loading-spinner">
          Chargement...
        </div>
      </div>
    );
  }


  // ==========================================================
  // INTERFACE
  // ==========================================================

  return (

    <div
      style={{
        padding: "24px",
      }}
    >

      {/* ====================================================
          HEADER
      ==================================================== */}

      <div className="page-header">

        <h1>
          Préparation d'Entretien IA
        </h1>

        <p className="page-subtitle">
          Générez des questions d'entretien
          personnalisées basées sur l'offre
          et le candidat
        </p>

      </div>


      {/* ====================================================
          ERROR
      ==================================================== */}

      {error && (

        <div className="alert alert-danger">

          <AlertCircle size={16} />

          {error}

        </div>

      )}


      {/* ====================================================
          SUCCESS
      ==================================================== */}

      {success && (

        <div className="alert alert-success">

          <CheckCircle size={16} />

          {success}

        </div>

      )}


      {/* ====================================================
          SELECTION
      ==================================================== */}

      <div className="card-inner-box">

        <div className="card-box-header">

          <h3>
            Sélection
          </h3>

        </div>


        <div className="form-row">

          {/* OFFRE */}

          <div className="form-group">

            <label>
              Offre d'emploi
            </label>

            <div className="input-with-icon">

              <Briefcase size={18} />

              <select
                value={
                  selectedJobOffer || ""
                }
                onChange={(event) => {

                  const value =
                    event.target.value;

                  setSelectedJobOffer(
                    value
                      ? Number(value)
                      : null
                  );

                }}
              >

                <option value="">
                  Sélectionner une offre...
                </option>

                {jobOffers.map(
                  (offer) => (

                    <option
                      key={offer.id}
                      value={offer.id}
                    >

                      {offer.title}

                      {" - "}

                      {offer.location ||
                        "Non spécifié"}

                    </option>

                  )
                )}

              </select>

            </div>

          </div>


          {/* CANDIDAT */}

          <div className="form-group">

            <label>
              Candidat (optionnel)
            </label>

            <div className="input-with-icon">

              <User size={18} />

              <select
                value={
                  selectedCandidate || ""
                }
                onChange={(event) => {

                  const value =
                    event.target.value;

                  setSelectedCandidate(
                    value
                      ? Number(value)
                      : null
                  );

                }}
              >

                <option value="">
                  Sélectionner un candidat...
                </option>

                {candidates.map(
                  (candidate) => (

                    <option
                      key={candidate.id}
                      value={candidate.id}
                    >

                      {candidate.first_name ||
                        candidate.prenom ||
                        ""}

                      {" "}

                      {candidate.last_name ||
                        candidate.nom ||
                        ""}

                      {candidate.email
                        ? ` - ${candidate.email}`
                        : ""}

                    </option>

                  )
                )}

              </select>

            </div>

          </div>

        </div>


        {/* ==================================================
            EXIGENCES
        ================================================== */}

        <div className="form-row">

          <div
            className="form-group full-width"
          >

            <label>
              Exigences personnalisées
              {" "}
              (optionnel)
            </label>

            <textarea
              value={
                customRequirements
              }
              onChange={(event) =>
                setCustomRequirements(
                  event.target.value
                )
              }
              placeholder="Ajoutez des exigences spécifiques pour cet entretien..."
              rows={3}
            />

          </div>

        </div>


        {/* ==================================================
            BOUTON
        ================================================== */}

        <div className="form-actions">

          <button
            type="button"
            className="btn btn-primary"
            onClick={
              handleGeneratePreparation
            }
            disabled={generating}
          >

            {generating ? (

              <>
                <Loader2
                  size={18}
                  className="spin"
                />

                Génération en cours...
              </>

            ) : (

              <>
                <Sparkles
                  size={18}
                />

                Générer l'entretien
              </>

            )}

          </button>

        </div>

      </div>


      {/* ====================================================
          RESULTATS
      ==================================================== */}

      {preparation && (

        <>

          {/* OFFRE */}

          {preparation.job_offer && (

            <div
              className="card-inner-box"
              style={{
                marginTop: "20px",
              }}
            >

              <div className="card-box-header">

                <h3>
                  Offre d'emploi
                </h3>

              </div>


              <div
                className="candidate-info-grid"
              >

                <div className="info-item">

                  <label>
                    Titre
                  </label>

                  <p>
                    {
                      preparation.job_offer
                        .title || "-"
                    }
                  </p>

                </div>


                <div className="info-item">

                  <label>
                    Compétences requises
                  </label>

                  <p>
                    {
                      preparation.job_offer
                        .skills || "-"
                    }
                  </p>

                </div>


                <div className="info-item">

                  <label>
                    Niveau d'expérience
                  </label>

                  <p>
                    {
                      preparation.job_offer
                        .experience_level || "-"
                    }
                  </p>

                </div>


                <div className="info-item">

                  <label>
                    Type de contrat
                  </label>

                  <p>
                    {
                      preparation.job_offer
                        .contract_type || "-"
                    }
                  </p>

                </div>

              </div>

            </div>

          )}


          {/* CANDIDAT */}

          {preparation.candidate && (

            <div
              className="card-inner-box"
              style={{
                marginTop: "20px",
              }}
            >

              <div className="card-box-header">

                <h3>
                  Candidat
                </h3>

              </div>


              <div
                className="candidate-info-grid"
              >

                <div className="info-item">

                  <label>
                    Nom
                  </label>

                  <p>
                    {
                      preparation.candidate
                        .first_name || ""
                    }

                    {" "}

                    {
                      preparation.candidate
                        .last_name || ""
                    }
                  </p>

                </div>


                <div className="info-item">

                  <label>
                    Email
                  </label>

                  <p>
                    {
                      preparation.candidate
                        .email || "-"
                    }
                  </p>

                </div>


                <div className="info-item">

                  <label>
                    Statut
                  </label>

                  <p>
                    {
                      preparation.candidate
                        .status || "-"
                    }
                  </p>

                </div>

              </div>

            </div>

          )}


          {/* MATCHING */}

          {preparation.matching && (

            <div
              className="card-inner-box"
              style={{
                marginTop: "20px",
              }}
            >

              <div className="card-box-header">

                <h3>
                  Matching CV/Offre
                </h3>

              </div>


              <div
                className="candidate-info-grid"
              >

                <div className="info-item">

                  <label>
                    Score de matching
                  </label>

                  <p
                    style={{
                      fontWeight: "bold",
                    }}
                  >

                    {
                      preparation.matching
                        .matching_score ?? 0
                    }

                    /100

                  </p>

                </div>


                <div className="info-item">

                  <label>
                    Compétences correspondantes
                  </label>

                  <p>

                    {
                      preparation.matching
                        .matched_skills
                        ?.join(", ") || "-"
                    }

                  </p>

                </div>


                <div className="info-item">

                  <label>
                    Compétences manquantes
                  </label>

                  <p>

                    {
                      preparation.matching
                        .missing_skills
                        ?.join(", ") || "-"
                    }

                  </p>

                </div>


                <div className="info-item">

                  <label>
                    Points forts
                  </label>

                  <p>

                    {
                      preparation.matching
                        .strengths
                        ?.join(", ") || "-"
                    }

                  </p>

                </div>

              </div>

            </div>

          )}


          {/* QUESTIONS */}

          <div
            className="card-inner-box"
            style={{
              marginTop: "20px",
            }}
          >

            <div className="card-box-header">

              <h3>

                Questions d'entretien
                {" ("}
                {
                  preparation.questions
                    ?.length || 0
                }
                {")"}

              </h3>

              <p
                style={{
                  marginTop: "5px",
                  fontSize: "14px",
                  color: "#6b7280",
                }}
              >

                Score total maximum :
                {" "}
                {
                  preparation.total_max_score ||
                  0
                }

              </p>

            </div>


            <div className="questions-container">

              {preparation.questions?.length ? (

                preparation.questions.map(
                  (question, index) => {

                    const category =
                      question.category ||
                      "TECHNICAL";

                    const isExpanded =
                      expandedCategories[
                        category
                      ];


                    return (

                      <div
                        key={
                          question.id ||
                          index
                        }
                        className="question-card"
                      >

                        <div
                          className="question-header"
                          onClick={() =>
                            toggleCategory(
                              category
                            )
                          }
                        >

                          <div
                            className="question-number"
                          >

                            <span
                              style={{
                                backgroundColor:
                                  getCategoryColor(
                                    category
                                  ),
                                color:
                                  "white",
                                padding:
                                  "4px 12px",
                                borderRadius:
                                  "12px",
                                fontSize:
                                  "12px",
                                fontWeight:
                                  "600",
                              }}
                            >

                              {index + 1}

                            </span>

                          </div>


                          <div
                            className="question-title"
                          >

                            <h4>
                              {
                                question.question_text ||
                                question.question ||
                                "-"
                              }
                            </h4>


                            <div
                              className="question-meta"
                            >

                              <span
                                className="category-badge"
                                style={{
                                  backgroundColor:
                                    getCategoryColor(
                                      category
                                    ) + "20",
                                  color:
                                    getCategoryColor(
                                      category
                                    ),
                                }}
                              >

                                {
                                  getCategoryLabel(
                                    category
                                  )
                                }

                              </span>


                              {question.skill && (

                                <span
                                  className="skill-badge"
                                >
                                  {
                                    question.skill
                                  }
                                </span>

                              )}


                              <span
                                className="difficulty-badge"
                                style={{
                                  backgroundColor:
                                    getDifficultyColor(
                                      question.difficulty
                                    ) + "20",
                                  color:
                                    getDifficultyColor(
                                      question.difficulty
                                    ),
                                }}
                              >

                                {
                                  getDifficultyLabel(
                                    question.difficulty
                                  )
                                }

                              </span>


                              <span
                                className="score-badge"
                              >

                                Note max :
                                {" "}
                                {
                                  question.max_score ??
                                  0
                                }

                              </span>

                            </div>

                          </div>


                          <div
                            className="question-toggle"
                          >

                            {isExpanded ? (

                              <ChevronUp
                                size={20}
                              />

                            ) : (

                              <ChevronDown
                                size={20}
                              />

                            )}

                          </div>

                        </div>


                        {isExpanded && (

                          <div
                            className="question-details"
                          >

                            {question.rationale && (

                              <div
                                className="detail-item"
                              >

                                <label>
                                  Pourquoi cette question :
                                </label>

                                <p>
                                  {
                                    question.rationale
                                  }
                                </p>

                              </div>

                            )}


                            {question.expected_elements && (

                              <div
                                className="detail-item"
                              >

                                <label>
                                  Éléments attendus :
                                </label>

                                <p>
                                  {
                                    question.expected_elements
                                  }
                                </p>

                              </div>

                            )}


                            {question.source_type && (

                              <div
                                className="detail-item"
                              >

                                <label>
                                  Source :
                                </label>

                                <p>

                                  {
                                    question.source_type ===
                                    "CV"
                                      ? "CV du candidat"
                                      : question.source_type ===
                                        "JOB_OFFER"
                                        ? "Offre d'emploi"
                                        : "Généré par IA"
                                  }

                                  {
                                    question.source_reference
                                      ? ` - ${question.source_reference}`
                                      : ""
                                  }

                                </p>

                              </div>

                            )}

                          </div>

                        )}

                      </div>

                    );
                  }
                )

              ) : (

                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#6b7280",
                  }}
                >
                  Aucune question générée.
                </div>

              )}

            </div>

          </div>


          {/* RECOMMANDATION */}

          {preparation.recommendation && (

            <div
              className="card-inner-box"
              style={{
                marginTop: "20px",
              }}
            >

              <div className="card-box-header">

                <h3>
                  Recommandation
                </h3>

              </div>

              <p
                style={{
                  lineHeight: "1.6",
                }}
              >
                {
                  preparation.recommendation
                }
              </p>

            </div>

          )}


          {/* DISTRIBUTION */}

          {preparation.categories_distribution && (

            <div
              className="card-inner-box"
              style={{
                marginTop: "20px",
              }}
            >

              <div className="card-box-header">

                <h3>
                  Distribution des catégories
                </h3>

              </div>


              <div
                className="categories-distribution"
              >

                {Object.entries(
                  preparation.categories_distribution
                ).map(
                  ([category, count]) => {

                    const totalQuestions =
                      preparation.questions
                        ?.length || 1;

                    const percentage =
                      (
                        Number(count) /
                        totalQuestions
                      ) * 100;


                    return (

                      <div
                        key={category}
                        className="category-stat"
                      >

                        <div
                          className="stat-bar"
                        >

                          <div
                            className="stat-fill"
                            style={{
                              width:
                                `${percentage}%`,
                              backgroundColor:
                                getCategoryColor(
                                  category
                                ),
                            }}
                          />

                        </div>


                        <div
                          className="stat-label"
                        >

                          <span>
                            {
                              getCategoryLabel(
                                category
                              )
                            }
                          </span>

                          <span
                            className="stat-count"
                          >
                            {count}
                          </span>

                        </div>

                      </div>

                    );
                  }
                )}

              </div>

            </div>

          )}


          {/* ACTIONS */}

          <div
            className="card-inner-box"
            style={{
              marginTop: "20px",
            }}
          >

            <div
              className="form-actions"
            >

              <button
                type="button"
                className="btn btn-secondary"
                onClick={
                  handleNewPreparation
                }
              >
                Nouvelle préparation
              </button>


              <button
                type="button"
                className="btn btn-primary"
                onClick={() =>
                  navigate(
                    "/rh/applications"
                  )
                }
              >

                <ArrowRight
                  size={18}
                />

                Aller aux candidatures

              </button>

            </div>

          </div>

        </>

      )}

    </div>

  );
};


export default InterviewPreparation;